import type { AttackProfile, Skill, SkillKind } from '../content/types.ts';
import { CUSTOM_CONTENT_ID_PREFIX, isCustomContentId } from './customContentId.ts';
import { STAT_IDS } from './stats.ts';
import { isDiceNotation } from '../rules/damageDice.ts';
import { DAMAGE_TYPES, isDamageType } from '../rules/damage.ts';

/**
 * The player's own Skills.
 *
 * A custom Skill is an ordinary `Skill`. It is not a special kind of thing that
 * characters have to know about: it goes into the same catalogue as the printed
 * Skills, is referenced by a crawler with the same `{ skillId, rank }` record,
 * rolls through the same Check pathway, and is marked for advancement by the
 * same advancement system. The ONLY differences are where the definition is
 * stored and that its `source` says `custom` rather than naming a book.
 *
 * Definitions live here, apart from any crawler. A crawler owning a custom
 * Skill stores only its ID and Rank, exactly as it does for a printed Skill, so
 * two crawlers can share one custom Skill and editing it updates both.
 *
 * RULE DCC-CUSTOM-001 - a custom Skill's ID is assigned once and never changes,
 * including when its displayed name is edited. Save files reference IDs.
 */

export const CUSTOM_SKILL_LIBRARY_SCHEMA_VERSION = 3;

/**
 * The custom ID namespace, shared with every other custom content type.
 * Re-exported under the old names so nothing that referred to Skill-specific
 * helpers has to change, and so the two cannot drift apart.
 */
export {
  CUSTOM_CONTENT_ID_PREFIX as CUSTOM_SKILL_ID_PREFIX,
  isCustomContentId as isCustomSkillId,
  makeCustomContentId as makeCustomSkillId,
  makeUniqueCustomContentId as makeUniqueCustomSkillId,
} from './customContentId.ts';

export interface CustomSkillLibrary {
  kind: 'custom-skill-library';
  schemaVersion: number;
  skills: Skill[];
  /**
   * RULE DCC-CUSTOM-006 - the optional Attack half of an Attack Skill.
   *
   * SEPARATE FROM THE SKILL, exactly as core content separates them. A `Skill`
   * says what the thing IS; an `AttackProfile` says what rolling it does. The
   * engine already reads them as two authorities, and flattening the profile
   * onto `Skill` to dodge this migration would have created a third shape only
   * custom content used.
   *
   * The profile's id EQUALS its Skill's id, which is how core content links
   * them, so `contentRegistry` merges these into the same lookup the Attack
   * pathway already uses. There is no custom-Attack execution path.
   *
   * OPTIONAL, and that is the point. A melee Skill with no profile here is a
   * perfectly valid REPRESENTED Skill that the player resolves by hand. Only a
   * Skill the player asked to automate gets one.
   */
  attacks: AttackProfile[];
  createdAt: string;
  updatedAt: string;
}

export function createCustomSkillLibrary(now: string): CustomSkillLibrary {
  return {
    kind: 'custom-skill-library',
    schemaVersion: CUSTOM_SKILL_LIBRARY_SCHEMA_VERSION,
    skills: [],
    attacks: [],
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * RULE DCC-CUSTOM-003 - the kinds a player may create.
 *
 * The full project identity set. A kind is REPRESENTATION; automation is a
 * separate, optional decision recorded elsewhere.
 */
export const CUSTOM_SKILL_KINDS: readonly SkillKind[] = [
  'utility',
  'passive',
  'melee-attack',
  'ranged-attack',
  'spell',
];

/** The four advancement behaviours the engine implements. */
export const SKILL_ADVANCEMENTS: readonly string[] = [
  'normal-check-use',
  'never-through-use',
  'explicit-trigger',
  'hand-to-hand-choice',
];

export const MAX_CUSTOM_SKILL_NOTES_LENGTH = 4000;
export const MAX_CUSTOM_SKILL_UPGRADE_LENGTH = 1000;

/**
 * The record fields: Rank Upgrades, Special Rules and display details.
 *
 * PERMISSIVE ABOUT MEANING, STRICT ABOUT SHAPE. This is the escape hatch, so
 * the text may say anything at all - the only questions asked are "is it text"
 * and "is it a sane length". Rejecting a Rank 15 upgrade because the engine
 * cannot execute it would defeat the entire point of recording it.
 */
function checkRecordFields(
  skill: Record<string, unknown>,
  at: string,
  push: (message: string) => void,
): void {
  const upgrades = skill['rankUpgrades'];
  if (upgrades !== undefined) {
    if (typeof upgrades !== 'object' || upgrades === null || Array.isArray(upgrades)) {
      push(`${at} rankUpgrades must be an object.`);
    } else {
      for (const rank of ['rank5', 'rank10', 'rank15'] as const) {
        const text = (upgrades as Record<string, unknown>)[rank];
        if (typeof text !== 'string') {
          push(`${at} rankUpgrades.${rank} must be text (empty is fine).`);
        } else if (text.length > MAX_CUSTOM_SKILL_UPGRADE_LENGTH) {
          push(`${at} rankUpgrades.${rank} is longer than ${MAX_CUSTOM_SKILL_UPGRADE_LENGTH}.`);
        }
      }
    }
  }

  const notes = skill['notes'];
  if (notes !== undefined) {
    if (typeof notes !== 'string') {
      push(`${at} notes must be text.`);
    } else if (notes.length > MAX_CUSTOM_SKILL_NOTES_LENGTH) {
      push(`${at} notes is longer than ${MAX_CUSTOM_SKILL_NOTES_LENGTH} characters.`);
    }
  }

  const details = skill['details'];
  if (details === undefined) return;
  if (typeof details !== 'object' || details === null || Array.isArray(details)) {
    push(`${at} details must be an object.`);
    return;
  }
  const d = details as Record<string, unknown>;
  for (const field of ['range', 'duration', 'cooldown', 'limitations'] as const) {
    if (d[field] !== undefined && typeof d[field] !== 'string') {
      push(`${at} details.${field} must be text.`);
    }
  }
  if (d['tags'] !== undefined) {
    if (!Array.isArray(d['tags'])) {
      push(`${at} details.tags must be a list.`);
    } else if (!d['tags'].every((tag) => typeof tag === 'string')) {
      push(`${at} details.tags must all be text.`);
    }
  }
  if (d['aiFavor'] !== undefined && (typeof d['aiFavor'] !== 'number' || !Number.isInteger(d['aiFavor']))) {
    push(`${at} details.aiFavor must be a whole number.`);
  }
  const checkType = d['checkType'];
  if (
    checkType !== undefined &&
    checkType !== 'unopposed' &&
    checkType !== 'opposed' &&
    checkType !== 'manual'
  ) {
    push(`${at} details.checkType must be unopposed, opposed or manual.`);
  }
}

export interface LibraryValidation {
  valid: boolean;
  problems: string[];
}

/**
 * Checks a record read off the disk before the application trusts it.
 *
 * Deliberately strict about the Skill fields the app relies on, because a
 * malformed custom Skill would otherwise surface as a crash deep inside a
 * Check rather than as a refusal to load the library.
 */
export function validateCustomSkillLibrary(raw: unknown): LibraryValidation {
  const problems: string[] = [];
  const push = (m: string) => problems.push(m);

  if (typeof raw !== 'object' || raw === null) {
    return { valid: false, problems: ['The custom Skill library is not an object.'] };
  }
  const lib = raw as Record<string, unknown>;

  if (lib['kind'] !== 'custom-skill-library') push('kind must be "custom-skill-library".');
  if (typeof lib['schemaVersion'] !== 'number') push('schemaVersion must be a number.');
  if (typeof lib['createdAt'] !== 'string') push('createdAt must be a string.');
  if (typeof lib['updatedAt'] !== 'string') push('updatedAt must be a string.');

  const skills = lib['skills'];
  if (!Array.isArray(skills)) {
    push('skills must be a list.');
    return { valid: problems.length === 0, problems };
  }

  const seen = new Set<string>();
  skills.forEach((entry, i) => {
    checkSkill(entry, i, seen, push);
  });

  // RULE DCC-CUSTOM-006 - the optional Attack half.
  const attacks = lib['attacks'];
  if (!Array.isArray(attacks)) {
    push('attacks must be a list.');
    return { valid: problems.length === 0, problems };
  }
  const attackIds = new Set<string>();
  const skillsById = new Map<string, Record<string, unknown>>();
  for (const entry of skills) {
    if (isPlainObject(entry)) {
      const skill = entry as Record<string, unknown>;
      if (typeof skill['id'] === 'string') skillsById.set(skill['id'], skill);
    }
  }
  attacks.forEach((entry, i) => {
    checkAttackProfile(entry, i, attackIds, skillsById, push);
  });

  return { valid: problems.length === 0, problems };
}

/**
 * Checks one custom AttackProfile.
 *
 * STRICT ABOUT STRUCTURE, because the Attack pathway reads these exactly as it
 * reads core profiles - `resolveAttackAction` indexes `baseDamage.dice` and
 * `toHitStat` without checking, so anything that gets past here is trusted for
 * the rest of the run.
 *
 * The profile must belong to a Skill in THIS library, and that Skill must be an
 * Attack kind. A profile whose Skill vanished is an orphan the registry would
 * merge into the attack lookup with nothing to invoke it.
 */
function checkAttackProfile(
  entry: unknown,
  i: number,
  attackIds: Set<string>,
  skillsById: Map<string, Record<string, unknown>>,
  push: (message: string) => void,
): void {
  const at = `attacks[${i}]`;
  if (!isPlainObject(entry)) {
    push(`${at} is not an object.`);
    return;
  }
  const profile = entry as Record<string, unknown>;

  // IDENTITY, and the Skill it belongs to. RULE DCC-CUSTOM-006 - the profile's
  // id EQUALS its Skill's id, which is the link core content uses.
  let skill: Record<string, unknown> | undefined;
  const id = profile['id'];
  if (typeof id !== 'string' || !isCustomContentId(id)) {
    push(`${at} has an id outside the custom namespace.`);
  } else if (attackIds.has(id)) {
    push(`${at} repeats the id "${id}".`);
  } else {
    attackIds.add(id);
    skill = skillsById.get(id);
    if (!skill) push(`${at} has no matching custom Skill.`);
  }

  // ATTACK TYPE, AND IT MUST MATCH THE SKILL'S KIND.
  //
  // The core catalogue pairs them: a melee Skill has a melee profile, a Spell
  // has a spell profile. Utility and Passive Skills have none at all - there is
  // no Attack to roll - so a profile attached to one is content the Attack
  // pathway would offer with nothing behind it. A Skill may still exist WITHOUT
  // a profile; that is how a manual Attack Skill is represented.
  const attackType = profile['attackType'];
  const expected = skill ? PROFILE_TYPE_FOR_KIND[String(skill['kind'])] : undefined;
  if (attackType !== 'melee' && attackType !== 'ranged' && attackType !== 'spell') {
    push(`${at} must be a melee, ranged or spell Attack.`);
  } else if (skill && expected === undefined) {
    push(`${at} belongs to a ${String(skill['kind'])} Skill, which has no Attack to roll.`);
  } else if (skill && attackType !== expected) {
    push(`${at} is a ${attackType} Attack on a ${String(skill['kind'])} Skill.`);
  }

  // RULE DCC-SKILL-006 - PASSIVE AND EXECUTABLE ATTACK CANNOT COEXIST.
  //
  // `passive` means the Skill makes no Skill Check (RULE DCC-SKILL-002, the E6
  // ruling). An AttackProfile means the application has executable Attack CHECK
  // mechanics for it. One says there is no roll; the other is the roll. A
  // record holding both would reach `resolveAttackAction`, which rolls an
  // Attack Check for a Skill whose own data says it does not make one.
  //
  // Kind-independent, because the contradiction is between the TRAIT and the
  // profile, not between the kind and anything.
  //
  // This also gives Attack-versus-Simple-Heal exclusivity for free, and
  // deliberately so: `supportEffect` already requires the `passive` trait, so a
  // Spell cannot be an executable Heal and an executable Attack at once. No
  // stored "automation mode" discriminator is needed - a third field saying
  // which of two authored facts is true would be a third thing to disagree.
  //
  // A player may still REPRESENT bizarre content: drop the profile and write it
  // in `description` or `notes`. What they cannot do is attach the engine's
  // Attack machinery to a Skill whose authored traits say it makes no Check.
  if (skill && Array.isArray(skill['traits']) && (skill['traits'] as unknown[]).includes('passive')) {
    push(`${at} belongs to a Passive Skill, which makes no Check and cannot roll an Attack.`);
  }
  const isSpell = attackType === 'spell';

  if (!(STAT_IDS as readonly string[]).includes(profile['toHitStat'] as string)) {
    push(`${at} needs a Stat to hit with.`);
  }

  // BASE DAMAGE. The dice go through the SHARED notation authority, because
  // "nonblank text" lets `dice: 'cats'` past and the damage roller then throws
  // inside `parseDice` at the worst possible moment.
  const damage = profile['baseDamage'];
  if (!isPlainObject(damage)) {
    push(`${at} needs base damage.`);
  } else {
    const d = damage as Record<string, unknown>;
    if (!isDiceNotation(d['dice'])) {
      push(`${at} base damage dice must be notation such as "1d6".`);
    }
    if (d['stat'] !== null && !(STAT_IDS as readonly string[]).includes(d['stat'] as string)) {
      push(`${at} has a damage Stat that is not a Stat.`);
    }
    // A REAL DCC damage type, through the shared authority - resistance,
    // vulnerability and immunity are all keyed on it, so `type: 'goat'` is
    // damage nothing can ever resist. Note the asymmetry with
    // `Skill.details.tags`, where "Goat Magic" is perfectly valid: tags are
    // read by people, this is read by `mitigateDamage`.
    if (!isDamageType(d['type'])) {
      push(`${at} damage type must be one of ${DAMAGE_TYPES.join(', ')}.`);
    }
  }

  // RANGE. The existing `AttackRange` contract, not a custom-only shape.
  const range = profile['range'];
  if (range !== null) {
    if (!isPlainObject(range)) {
      push(`${at} range must be a range object or null.`);
    } else {
      const r = range as Record<string, unknown>;
      if (r['feet'] !== undefined && (typeof r['feet'] !== 'number' || !Number.isInteger(r['feet']) || r['feet'] < 0)) {
        push(`${at} range feet must be a whole number of at least 0.`);
      }
      if (r['kind'] !== undefined && r['kind'] !== 'melee' && r['kind'] !== 'line-of-sight') {
        push(`${at} range kind must be melee or line-of-sight.`);
      }
    }
  }

  const aiFavor = profile['aiFavor'];
  if (aiFavor !== null) {
    if (!isPlainObject(aiFavor)) {
      push(`${at} AI Favor must be an object or null.`);
    } else {
      const f = aiFavor as Record<string, unknown>;
      if (typeof f['amount'] !== 'number' || !Number.isInteger(f['amount']) || f['amount'] < 0) {
        push(`${at} AI Favor amount must be a whole number of at least 0.`);
      }
      if (f['condition'] !== null && typeof f['condition'] !== 'string') {
        push(`${at} AI Favor condition must be text or null.`);
      }
    }
  }

  // Strings, not merely "an array": `planAttackFor` spreads these into a list
  // it then renders.
  const limitations = profile['limitations'];
  if (!Array.isArray(limitations) || !limitations.every((l) => typeof l === 'string')) {
    push(`${at} limitations must be a list of text.`);
  }

  // `cooldownScopeFor` calls `.toLowerCase()` on this. A number reaches that
  // and throws.
  const cooldown = profile['cooldown'];
  if (cooldown !== null && typeof cooldown !== 'string') {
    push(`${at} cooldown must be text or null.`);
  }

  // MANA. RULE DCC-SPELL-006 - ONE PRICE PER SPELL. The Attack pathway reads
  // the profile while the Hotlist and the cast presentation read the Skill, so
  // a Skill saying 8 and a profile charging 3 would show the crawler two
  // different costs for the same Spell depending on where they looked.
  const manaCost = profile['manaCost'];
  if (isSpell) {
    if (typeof manaCost !== 'number' || !Number.isInteger(manaCost) || manaCost < 0) {
      push(`${at} needs a whole Mana cost of at least 0.`);
    } else if (skill && skill['manaCost'] !== manaCost) {
      push(`${at} charges ${manaCost} Mana but its Spell costs ${String(skill['manaCost'])}.`);
    }
  } else if (manaCost !== null) {
    push(`${at} must have no Mana cost.`);
  }

  const area = profile['area'];
  if (area !== null) {
    if (!isPlainObject(area)) {
      push(`${at} area must be an object or null.`);
    } else {
      const a = area as Record<string, unknown>;
      if (typeof a['kind'] !== 'string' || a['kind'].trim() === '') {
        push(`${at} area needs a kind.`);
      }
      if (typeof a['feet'] !== 'number' || !Number.isInteger(a['feet']) || a['feet'] < 1) {
        push(`${at} area feet must be a whole number of at least 1.`);
      }
    }
  }

  // RANK 5 is the AUTHORED Attack automation, and is not the freeform
  // `Skill.rankUpgrades.rank5` prose. Nothing parses one into the other.
  const rank5 = profile['rank5'];
  if (!isPlainObject(rank5)) {
    push(`${at} needs a rank5 entry (its notes may be empty).`);
  } else {
    const r5 = rank5 as Record<string, unknown>;
    if (r5['addedDamageDice'] !== null && !isDiceNotation(r5['addedDamageDice'])) {
      push(`${at} rank5 added damage must be dice notation or null.`);
    }
    if (!Array.isArray(r5['notes']) || !r5['notes'].every((n) => typeof n === 'string')) {
      push(`${at} rank5 notes must be a list of text.`);
    }
  }

  // WEAPON USE - required, and nullable. `null` is the truthful answer for a
  // claw, a bite or a Spell, and the core catalogue already uses it for
  // fourteen profiles. ABSENCE is still not an answer (RULE DCC-INV-017).
  if (!('weaponUse' in profile)) {
    push(`${at} must say what it is used with (null when it is not a weapon).`);
  } else {
    const weaponUse = profile['weaponUse'];
    if (weaponUse !== null) {
      if (!isPlainObject(weaponUse)) {
        push(`${at} weaponUse must be a held-weapon object or null.`);
      } else {
        const w = weaponUse as Record<string, unknown>;
        if (w['kind'] !== 'held-weapon') push(`${at} weaponUse kind must be "held-weapon".`);
        if (w['hands'] !== 1 && w['hands'] !== 2) push(`${at} weaponUse hands must be 1 or 2.`);
      }
      if (isSpell) push(`${at} is a Spell Attack and cannot be held.`);
    }
  }

  // SOURCE. A player-created Attack has no printed page, and inventing one is
  // what `CustomSource` exists to prevent.
  const source = profile['source'];
  if (!isPlainObject(source)) {
    push(`${at} has no source.`);
  } else {
    const src = source as Record<string, unknown>;
    if (src['book'] !== 'custom') push(`${at} must have a custom source.`);
    if (typeof src['createdAt'] !== 'string' || src['createdAt'].trim() === '') {
      push(`${at} must record when it was created.`);
    }
  }
}

function isPlainObject(value: unknown): boolean {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Which AttackProfile type each Skill kind may carry, per the core catalogue. */
const PROFILE_TYPE_FOR_KIND: Record<string, string | undefined> = {
  'melee-attack': 'melee',
  'ranged-attack': 'ranged',
  spell: 'spell',
};

/**
 * Checks EVERY field the application reads off a Skill.
 *
 * The bar is not "does this resemble a Skill" but "can every consumer of a
 * Skill handle this". A file the player or another tool has edited by hand
 * reaches the registry through here and nowhere else, so anything this lets
 * past is trusted for the rest of the run. Two concrete consequences:
 *
 *  - the interface indexes STAT_LABELS by `commonStat`, so an arbitrary string
 *    there would render as undefined and crash on `.abbr`;
 *  - the advancement system switches on `advancement`, so a value outside the
 *    derived behaviour for the kind would make a Passive Skill mark itself.
 *
 * A Skill that is mechanically impossible is rejected even when its JSON is
 * well formed.
 */
function checkSkill(
  entry: unknown,
  i: number,
  seen: Set<string>,
  push: (message: string) => void,
): void {
  const at = `skills[${i}]`;
  if (typeof entry !== 'object' || entry === null) {
    push(`${at} is not an object.`);
    return;
  }
  const skill = entry as Record<string, unknown>;

  // Identity.
  const id = skill['id'];
  if (typeof id !== 'string' || id === '') {
    push(`${at} has no id.`);
  } else if (!isCustomContentId(id)) {
    push(`${at} has id "${id}", which is not in the custom namespace.`);
  } else if (id === CUSTOM_CONTENT_ID_PREFIX) {
    push(`${at} has an id that is only the prefix.`);
  } else if (seen.has(id)) {
    push(`${at} repeats the id "${id}".`);
  } else {
    seen.add(id);
  }

  const name = skill['name'];
  if (typeof name !== 'string' || name.trim() === '') {
    push(`${at} has no name.`);
  }

  // Kind. RULE DCC-CUSTOM-003 - the FULL project identity set.
  //
  // WIDENED IN E7, from utility|passive. Dungeon Crawler Carl explicitly
  // expects players to build their own Skills and Spells (C&C p.42), and
  // refusing to store an Attack Skill or a Spell forced them into JSON. What a
  // kind does NOT promise is automation: a melee Skill with no AttackProfile is
  // a valid represented Skill the player rolls by hand.
  const kind = skill['kind'];
  const kindOk = (CUSTOM_SKILL_KINDS as readonly string[]).includes(kind as string);
  if (!kindOk) {
    push(`${at} must be one of ${CUSTOM_SKILL_KINDS.join(', ')}.`);
  }
  const passive = Array.isArray(skill['traits']) && (skill['traits'] as unknown[]).includes('passive');

  // Common Stat. DCC-CUSTOM-004, and the reason the interface can index
  // STAT_LABELS without checking.
  const commonStat = skill['commonStat'];
  if (commonStat !== null && !(STAT_IDS as readonly string[]).includes(commonStat as string)) {
    push(`${at} has "${String(commonStat)}" as its Common Stat, which is not a Stat.`);
  } else if (kind === 'utility' && commonStat === null) {
    push(`${at} is a Utility Skill and must have a Common Stat.`);
  } else if (kind === 'passive' && commonStat !== null) {
    push(`${at} is a Passive Skill and must not have a Common Stat.`);
  } else if (passive && kind !== 'passive' && commonStat !== null) {
    // RULE DCC-SKILL-002 - `passive` is a TRAIT, not a kind. Heal is a Spell
    // that makes no Check, and a Spell that makes no Check has no Stat to make
    // it with.
    push(`${at} makes no Skill Check and must not have a Common Stat.`);
  }

  // Advancement. EXPLICIT, from the four the engine implements - never inferred
  // from the kind, which is RULE DCC-SKILL-002's other half. What IS enforced
  // is that the engine can honour it: a Skill that makes no Check cannot
  // advance through using one.
  const advancement = skill['advancement'];
  if (!(SKILL_ADVANCEMENTS as readonly string[]).includes(advancement as string)) {
    push(`${at} must advance by one of ${SKILL_ADVANCEMENTS.join(', ')}.`);
  } else if (passive && advancement === 'normal-check-use') {
    push(`${at} makes no Skill Check, so it cannot advance through normal use.`);
  }
  const advancementNote = skill['advancementNote'];
  if (advancementNote !== null && typeof advancementNote !== 'string') {
    push(`${at} advancement note must be text or null.`);
  }

  // RULE DCC-SKILL-002 - traits are the two the ENGINE reads. `passive` decides
  // whether a Check happens; `interrupt` decides when the Action is legal.
  // Descriptive words go in `details.tags`, where nothing consults them.
  const traits = skill['traits'];
  if (!Array.isArray(traits)) {
    push(`${at} traits must be a list.`);
  } else {
    for (const trait of traits) {
      if (trait !== 'passive' && trait !== 'interrupt') {
        push(`${at} has "${String(trait)}" as a trait; only passive and interrupt are traits.`);
      }
    }
    if (kind === 'passive' && !traits.includes('passive')) {
      push(`${at} is a Passive Skill and must carry the passive trait.`);
    }
  }

  // Mana belongs to Spells and nothing else.
  // RULE DCC-SPELL-007 - a Spell's Mana cost is EXPLICIT, never absent.
  //
  // Rules p.19: casting spends "the Mana cost noted in the Spell's
  // description", and every printed Spell authors one. `null` would leave the
  // editor and the Hotlist guessing whether a Spell is free or simply
  // unfinished. Three unambiguous states instead:
  //
  //   null  this definition is not a Spell
  //   0     this Spell explicitly costs no Mana
  //   N     this Spell costs N
  //
  // A GM inventing a free Spell writes 0, which is a decision. `null` was not.
  const manaCost = skill['manaCost'];
  if (kind === 'spell') {
    if (typeof manaCost !== 'number' || !Number.isInteger(manaCost) || manaCost < 0) {
      push(`${at} is a Spell and needs a Mana cost of 0 or more (use 0 for a free Spell).`);
    }
  } else if (manaCost !== null) {
    push(`${at} must have no Mana cost.`);
  }

  const maxRank = skill['maxRank'];
  if (maxRank !== undefined && (typeof maxRank !== 'number' || !Number.isInteger(maxRank) || maxRank < 1)) {
    push(`${at} max Rank must be a whole number of at least 1.`);
  }

  if (skill['restriction'] !== null) {
    push(`${at} must have no restriction.`);
  }

  // RULE DCC-SPELL-003 - the executable support effect, a CLOSED union.
  //
  // Unvalidated while custom content could not create Spells, and dangerous the
  // moment it could: `castSupportSpell` branches on
  // `supportEffect?.kind === 'heal-slots'` and then heals `effect.slots`
  // directly, so a hand-edited `slots: 'a lot'` becomes trusted content.
  //
  // Only on a Spell, and only on one that makes no Skill Check - the current
  // support-cast pathway resolves without a roll. `interrupt` is NOT required:
  // a healing Spell may be Passive without being castable out of turn.
  const supportEffect = skill['supportEffect'];
  if (supportEffect !== undefined) {
    if (kind !== 'spell') {
      push(`${at} is not a Spell, so it cannot carry a support effect.`);
    } else if (!passive) {
      push(`${at} makes a Skill Check, so its support effect could not be resolved.`);
    }
    if (!isPlainObject(supportEffect)) {
      push(`${at} support effect must be an object.`);
    } else {
      const effect = supportEffect as Record<string, unknown>;
      if (effect['kind'] === 'heal-full') {
        if (effect['target'] !== 'pet') push(`${at} full-healing support effect can only target a pet.`);
      } else if (effect['kind'] !== 'heal-slots') {
        push(`${at} has an unsupported support effect "${String(effect['kind'])}".`);
      } else {
        const slots = effect['slots'];
        if (typeof slots !== 'number' || !Number.isInteger(slots) || slots < 1) {
          push(`${at} support effect must heal a whole number of at least 1 slot.`);
        }
        if (effect['target'] !== 'self') {
          push(`${at} support effect can only target self.`);
        }
      }
    }
  }

  checkRecordFields(skill, at, push);

  // Description is optional, but must be text of a sane length when present.
  const description = skill['description'];
  if (description !== undefined) {
    if (typeof description !== 'string') {
      push(`${at} description must be text.`);
    } else if (description.length > MAX_CUSTOM_SKILL_DESCRIPTION_LENGTH) {
      push(`${at} description is longer than ${MAX_CUSTOM_SKILL_DESCRIPTION_LENGTH} characters.`);
    }
  }

  // Source.
  const source = skill['source'];
  if (typeof source !== 'object' || source === null) {
    push(`${at} has no source.`);
    return;
  }
  const src = source as Record<string, unknown>;
  if (src['book'] !== 'custom') {
    push(`${at} must have a custom source.`);
  }
  if (typeof src['createdAt'] !== 'string' || src['createdAt'].trim() === '') {
    push(`${at} must record when it was created.`);
  }
}

/**
 * Kept here rather than imported from the rules layer, because the domain layer
 * must not depend on it. `src/rules/customSkills.ts` re-exports this so the two
 * limits cannot drift apart.
 */
export const MAX_CUSTOM_SKILL_DESCRIPTION_LENGTH = 2000;

type LibraryMigration = (record: Record<string, unknown>) => Record<string, unknown>;

/**
 * Migrations run in order from the record's own version up to the current one.
 * There are none yet; the map exists so that adding version 2 is a one-line
 * change rather than a redesign, exactly as for characters and sessions.
 */
/**
 * 2 -> 3: the library gained an Attack catalogue, and Skills gained record
 * fields (RULE DCC-SKILL-004/005, DCC-CUSTOM-006).
 *
 * NOTHING IS INVENTED. An empty `attacks` list is the truthful answer - the
 * build that wrote a version 2 library could not create an Attack Skill at all,
 * so no profile went unrecorded. The new Skill fields stay ABSENT rather than
 * being filled with empty strings: `rankUpgrades` absent means "nothing
 * recorded", and writing three empty strings would claim someone had looked.
 *
 * Existing Utility and Passive Skills pass through untouched - same ids, same
 * descriptions, same traits, same advancement - and no crawler's Rank is
 * involved, because Ranks were never stored here.
 */
function migrateSkillLibrary2to3(record: Record<string, unknown>): Record<string, unknown> {
  // AFTER the spread, and unconditionally. Schema 2 did not OWN this field, so
  // an `attacks` array hand-written into a version 2 record is unknown data,
  // not content - and by this migration's own rationale a real version 2 build
  // could not author an AttackProfile. Preserving it would hand a stray field
  // schema-3 mechanical meaning purely because it happened to be an array.
  // Same canonicalisation lesson as the portable-character migration.
  return { ...record, attacks: [] };
}

export const CUSTOM_SKILL_LIBRARY_MIGRATIONS: Record<number, LibraryMigration> = {
  1: migrateSkillLibrary1to2,
  2: migrateSkillLibrary2to3,
};

/**
 * Brings a stored library up to the current schema.
 * Returns null when there is nothing usable, which the caller treats as
 * "no custom Skills yet" rather than as an error.
 */
/**
 * 1 -> 2: a stored Passive custom Skill gains the `passive` TRAIT.
 *
 * RULE DCC-SKILL-002. Schema 1 carried "makes no Check" in `kind` alone, so
 * every schema-1 Passive Skill means exactly one thing and the trait is
 * derivable rather than guessed. `kind` does NOT move - a custom Passive Skill
 * is still categorically Passive; only the behaviour moved.
 *
 * Canonicalised AFTER the spread, so a hand-edited schema-1 file claiming
 * contradictory traits - `kind: 'passive'` with `traits: ['interrupt']`, say -
 * cannot carry them forward into a meaning schema 1 could not author.
 */
function migrateSkillLibrary1to2(record: Record<string, unknown>): Record<string, unknown> {
  const skills = record['skills'];
  if (!Array.isArray(skills)) return record;
  return {
    ...record,
    skills: skills.map((skill) => {
      if (typeof skill !== 'object' || skill === null) return skill;
      const entry = skill as Record<string, unknown>;
      return { ...entry, traits: entry['kind'] === 'passive' ? ['passive'] : [] };
    }),
  };
}

export function migrateCustomSkillLibraryRecord(raw: unknown): Record<string, unknown> | null {
  if (typeof raw !== 'object' || raw === null) return null;
  let record = { ...(raw as Record<string, unknown>) };

  const version = record['schemaVersion'];
  if (typeof version !== 'number') return null;
  if (version > CUSTOM_SKILL_LIBRARY_SCHEMA_VERSION) return null;

  let current = version;
  while (current < CUSTOM_SKILL_LIBRARY_SCHEMA_VERSION) {
    const step = CUSTOM_SKILL_LIBRARY_MIGRATIONS[current];
    if (!step) return null;
    record = step(record);
    current += 1;
    record['schemaVersion'] = current;
  }
  return record;
}
